import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, HUB_META } from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Broadcast } from "@phosphor-icons/react";

function defaultWhen() {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 30);
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function LiveNew() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [hubs, setHubs] = useState([]);
  const [form, setForm] = useState({
    topic: "",
    description: "",
    hub_key: "automotive",
    scheduled_at: defaultWhen(),
    visibility: "public",
    embed_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/hubs").then((r) => setHubs(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (user === false) nav("/login", { state: { from: "/live/new" } });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.topic.trim()) return setErr("Topic is required");
    setBusy(true);
    try {
      const { data } = await api.post("/sessions", {
        ...form,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
      });
      nav(`/live/${data.id}`);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message);
    } finally {
      setBusy(false);
    }
  };

  if (!user || user === false) return <AppShell><div className="py-20 text-center label-mono">...</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto" data-testid="live-new-page">
        <button onClick={() => nav("/live")} className="btn btn-ghost btn-sm mb-4">
          <ArrowLeft size={12} weight="bold" /> All sessions
        </button>
        <div className="label-mono mb-2 flex items-center gap-2" style={{ color: "#E60012" }}>
          <Broadcast size={11} weight="fill" /> NEW SESSION
        </div>
        <h1 className="font-display text-white mb-8" style={{ fontSize: 40, fontWeight: 900, letterSpacing: "-0.035em" }}>
          Schedule a live.
        </h1>

        <form onSubmit={submit} data-testid="live-new-form">
          <label className="label-mono block mb-1.5">TOPIC</label>
          <input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
            required className="input mb-4" placeholder="e.g. Hybrid-electric propulsion: 2027 path"
            data-testid="live-topic-input" />

          <label className="label-mono block mb-1.5">DESCRIPTION</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3} className="input mb-4 resize-none" placeholder="What will you cover?"
            data-testid="live-description-input" />

          <label className="label-mono block mb-1.5">HUB</label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            {hubs.map((h) => {
              const color = HUB_META[h.key]?.color || h.color || "#E60012";
              return (
                <button type="button" key={h.key} onClick={() => setForm({ ...form, hub_key: h.key })}
                  className={`p-3 border text-left transition-colors ${form.hub_key === h.key ? "border-white bg-[#1A1A1D]" : "border-[#27272A] hover:border-zinc-500"}`}
                  data-testid={`live-hub-${h.key}`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="hub-dot" style={{ color }} />
                    <span className="label-mono" style={{ color, fontSize: 9 }}>{HUB_META[h.key]?.short || h.key.toUpperCase().slice(0, 3)}</span>
                  </div>
                  <div className="text-xs text-white">{h.name}</div>
                </button>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="label-mono block mb-1.5">SCHEDULED AT (LOCAL)</label>
              <input type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                required className="input" data-testid="live-schedule-input" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">VISIBILITY</label>
              <div className="grid grid-cols-2 gap-2">
                {["public", "private"].map((v) => (
                  <button key={v} type="button" onClick={() => setForm({ ...form, visibility: v })}
                    className={`py-2.5 border text-xs font-mono uppercase tracking-wider ${
                      form.visibility === v ? "bg-[#E60012] border-[#E60012] text-white" : "border-[#27272A] text-zinc-400"
                    }`}
                    data-testid={`live-vis-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <label className="label-mono block mb-1.5">EMBED URL (OPTIONAL — YouTube/Zoom/Twitch)</label>
          <input value={form.embed_url} onChange={(e) => setForm({ ...form, embed_url: e.target.value })}
            className="input mb-6" placeholder="https://youtube.com/embed/..."
            data-testid="live-embed-input" />

          {err && <div className="text-xs text-red-500 mb-4 border border-red-900/40 px-3 py-2" data-testid="live-error">{err}</div>}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => nav("/live")} className="btn btn-ghost" data-testid="live-cancel-btn">Cancel</button>
            <button type="submit" disabled={busy} className="btn btn-primary" data-testid="live-create-btn">
              {busy ? <span className="spinner" /> : "Schedule session"}
            </button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
